import React, { useEffect, useRef } from 'react';
import './App.css';
import Logo from './components/Logo';
import WaitlistForm from './components/WaitlistForm';
import Infographic from './assets/ai-matching-infographic.png';

function Feature({ title, text }) {
  return (
    <div className="feature">
      <h3>{title}</h3>
      <p className="dim">{text}</p>
    </div>
  );
}

// Simple validators (match WaitlistForm rules)
const nameValid = (v) => /^[A-Za-z .'-]{2,}$/.test(v) && !/\d/.test(v);
const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function App() {
  const formHostRef = useRef(null);

  useEffect(() => {
    const host = formHostRef.current;
    if (!host) return;

    const formEl = host.querySelector('form');
    const emailInput = formEl?.querySelector('input[type="email"]') || null;
    const checkbox = formEl?.querySelector('input[type="checkbox"]') || null;
    const submitBtn = formEl?.querySelector('button[type="submit"]') || null;

    // Find the name input: first visible text input that is not email or checkbox
    let nameInput = null;
    const allInputs = formEl ? Array.from(formEl.querySelectorAll('input')) : [];
    for (const el of allInputs) {
      const t = (el.getAttribute('type') || 'text').toLowerCase();
      const isHiddenStyle = (el.style && el.style.display === 'none') || el.getAttribute('type') === 'hidden';
      if (!isHiddenStyle && t !== 'email' && t !== 'checkbox') {
        nameInput = el;
        break;
      }
    }

    // Inline email error node just for live typing feedback
    let liveEmailError = null;
    if (emailInput) {
      // reuse an existing sibling error area if needed, else create a dedicated one
      liveEmailError = emailInput.parentElement?.querySelector('.inline-email-live-error');
      if (!liveEmailError) {
        liveEmailError = document.createElement('div');
        liveEmailError.className = 'error-text inline-email-live-error';
        liveEmailError.style.display = 'none';
        emailInput.parentElement?.appendChild(liveEmailError);
      }
    }

    const isLoading = () =>
      submitBtn && typeof submitBtn.textContent === 'string' && submitBtn.textContent.startsWith('Submitting');

    const syncState = () => {
      if (!submitBtn) return;

      const nameOk = nameInput ? nameValid(nameInput.value.trim()) : false;
      const emailOk = emailInput ? emailValid(emailInput.value.trim()) : false;
      const consentOk = checkbox ? checkbox.checked : false;

      // Show live email error while typing
      if (liveEmailError) {
        const show = emailInput && emailInput.value.length > 0 && !emailOk;
        liveEmailError.textContent = show ? 'Enter a valid email address.' : '';
        liveEmailError.style.display = show ? 'block' : 'none';
      }

      // Keep the button disabled during loading regardless
      if (isLoading()) {
        submitBtn.disabled = true;
        return;
      }

      // Enable only when all three conditions are met
      submitBtn.disabled = !(nameOk && emailOk && consentOk);
    };

    // Initial sync and listeners
    syncState();
    nameInput?.addEventListener('input', syncState);
    emailInput?.addEventListener('input', syncState);
    checkbox?.addEventListener('change', syncState);

    // In case the child toggles loading text
    const mo = new MutationObserver(syncState);
    if (submitBtn) mo.observe(submitBtn, { childList: true, characterData: true, subtree: true });

    return () => {
      nameInput?.removeEventListener('input', syncState);
      emailInput?.removeEventListener('input', syncState);
      checkbox?.removeEventListener('change', syncState);
      mo.disconnect();
    };
  }, []);

  return (
    <>
      <header className="header">
        <div className="container header-inner" style={{ padding: '0 16px' }}>
          <Logo />
          <nav className="nav">
            <a href="#features">Features</a>
            <a href="#privacy">Privacy</a>
            <a href="#waitlist">Waitlist</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div>
              <h1>Find work that fits. <br /> Let AI do the heavy lifting.</h1>
              <p className="lead">
                CareerMove ranks roles against your skills, goals, and location. The matching engine
                learns from real outcomes then alerts you when a high quality fit is open. Faster
                searches. Fewer dead ends.
              </p>

              <div id="waitlist" className="card" ref={formHostRef}>
                <h2 style={{ marginTop: 0 }}>Join early access</h2>
                <p className="dim" style={{ marginTop: 6 }}>
                  Add your name and email to be first in line when matching goes live.
                </p>
                <WaitlistForm />
              </div>
            </div>

            {/* Right side: infographic image */}
            <div>
              <div
                className="illus"
                role="img"
                aria-label="AI matching infographic"
                style={{ display: 'grid', placeItems: 'center', overflow: 'hidden' }}
              >
                <img
                  src={Infographic}
                  alt="Infographic showing CareerMove's AI matching candidates to employers"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="features">
          <div className="container">
            <div className="features-grid">
              <Feature
                title="Matching that understands context"
                text="Models evaluate skills, experience, tools, compensation range, commute or remote, and culture fit signals to compute a rank that reflects real world success."
              />
              <Feature
                title="Signals for candidates and employers"
                text="Candidates get a short list of roles worth attention. Employers see a focused slate of people who can do the work today, not a pile of resumes."
              />
              <Feature
                title="Quality over volume"
                text="Less noise, more traction. The system learns from feedback to improve precision. Matching gets smarter with every accepted offer."
              />
            </div>
          </div>
        </section>

        <section id="seo" className="section">
          <div className="container">
            <h2 style={{ marginTop: 0 }}>AI powered job matching for real outcomes</h2>
            <p className="dim" style={{ maxWidth: 820 }}>
              CareerMove is a candidate first job platform that uses machine learning to connect
              people and teams with precision. The product analyzes profiles and roles, scores the
              fit, and notifies both sides when there is a strong match. This improves time to hire
              and reduces churn. Join the waitlist to get matched as soon as the service goes live.
            </p>
          </div>
        </section>

        <section id="privacy" className="section" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="container">
            <h3 style={{ marginTop: 0 }}>Privacy</h3>
            <p className="dim" style={{ maxWidth: 820 }}>
              We store the name and email you submit for early access. Data is used to contact you
              about availability and onboarding. You can request removal at any time.
            </p>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-inner" style={{ padding: '0 16px' }}>
          <Logo />
          <span className="dim">© {new Date().getFullYear()} CareerMove. All rights reserved.</span>
        </div>
      </footer>
    </>
  );
}
